import React, { Suspense, useEffect, useState } from 'react';
import { Route, Routes, Navigate, useLocation, HashRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useColorModes } from '@coreui/react';
import './scss/style.scss';

const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'));
const Login = React.lazy(() => import('./views/pages/login/Login'));
const ForgotPassword = React.lazy(() => import('./views/pages/forgot_password/ForgotPassword'));
const Profile = React.lazy(() => import('./views/pages/User/Profile'));
const Register = React.lazy(() => import('./views/pages/register/Register'));
const Page404 = React.lazy(() => import('./views/pages/page404/Page404'));
const Page500 = React.lazy(() => import('./views/pages/page500/Page500'));
const Mappage = React.lazy(() => import('./views/pages/map/MapComponent'));
const CreatePassword = React.lazy(() => import('./views/pages/login/CreatePassword'));

const getMobileAppFlag = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const searchValue = searchParams.get('mobile_app');
  if (searchValue === '1') return true;

  const hash = window.location.hash || '';
  const hashQueryIndex = hash.indexOf('?');

  if (hashQueryIndex !== -1) {
    const hashQuery = hash.substring(hashQueryIndex + 1);
    const hashParams = new URLSearchParams(hashQuery);
    return hashParams.get('mobile_app') === '1';
  }

  return false;
};

const applyAppEnvironmentClasses = () => {
  const isMobileApp = getMobileAppFlag();

  document.body.classList.toggle('mobile-app', isMobileApp);
  document.body.classList.toggle('web-app', !isMobileApp);

  return isMobileApp;
};

const ProtectedRoute = ({ children, requiredPermission }) => {
  const userSession = sessionStorage.getItem('user');
  const permissions = JSON.parse(sessionStorage.getItem('permissions')) || [];

  if (!userSession) {
    return <Navigate to="/login" />;
  }

  if (requiredPermission && !permissions.some((perm) => perm.name === requiredPermission)) {
    return <Navigate to="/404" />;
  }

  return children;
};

const AppContent = () => {
  const location = useLocation();
  const [elements, setElements] = useState([]);
  const [isMobileApp, setIsMobileApp] = useState(getMobileAppFlag());

  useEffect(() => {
    const savedElements = JSON.parse(sessionStorage.getItem('elements')) || [];
    setElements(savedElements);
    setIsMobileApp(getMobileAppFlag());
    applyAppEnvironmentClasses();
  }, [location]);

  return (
    <Suspense fallback={<div>Yükleniyor...</div>}>
      <Routes>
        <Route exact path="/login" name="Login Page" element={<Login />} />
        <Route exact path="/forgotpassword" name="Forgot Password Page" element={<ForgotPassword />} />
        <Route exact path="/profile" name="Profile" element={<Profile />} />
        <Route exact path="/register" name="Register Page" element={<Register />} />
        <Route exact path="/404" name="Page 404" element={<Page404 />} />
        <Route exact path="/500" name="Page 500" element={<Page500 />} />
        <Route exact path="/mappage" name="MapPage" element={<Mappage />} />
        <Route exact path="/createpassword" name="Create Password" element={<CreatePassword />} />

        {elements.map((element) => {
          const path = element?.name?.toLowerCase();
          if (!path) return null;

          return (
            <Route
              key={element.id}
              path={`/${path}`}
              element={
                <ProtectedRoute requiredPermission={`GET:/${path}`}>
                  <DefaultLayout isMobileApp={isMobileApp} />
                </ProtectedRoute>
              }
            />
          );
        })}

        <Route
          path="*"
          element={
            <ProtectedRoute>
              <DefaultLayout isMobileApp={isMobileApp} />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
};

const App = () => {
  const { isColorModeSet, setColorMode } = useColorModes('coreui-free-react-admin-template-theme');
  const storedTheme = useSelector((state) => state.theme);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const themeValue = params.get('theme');
    const theme = themeValue?.match(/^[A-Za-z0-9\s]+/)?.[0];

    if (theme) setColorMode(theme);
    if (!isColorModeSet()) setColorMode(storedTheme);

    applyAppEnvironmentClasses();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <HashRouter>
      <AppContent />
    </HashRouter>
  );
};

export default App;